import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import { Button, FormLabel, Grid2 as Grid, IconButton, OutlinedInput, Stack, Typography } from '@mui/material'
import { ICellRendererParams } from 'ag-grid-community'
import { useSnackbar } from 'notistack'
import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { AdminOnly } from '../components/AdminOnly'
import InviteUserDialog from '../components/InviteUserDialog'
import PageHeader from '../components/PageHeader'
import { ThemedAgGrid } from '../components/ThemedAgGrid'
import { useAuthContext } from '../contexts/Auth/useAuthContext'
import useEventStream from '../contexts/EventStream/useEventStream'
import { graphql } from '../gql'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { serializeDates } from '../utils/serialize-dates'

export default function OrganizationPage() {
  const authContext = useAuthContext()
  const org = useMemo(() => {
    return authContext.user?.orgs.find((org) => org.id === authContext.activeOrganization)
  }, [authContext])
  useDocumentTitle(org?.name || 'Organization')
  const { enqueueSnackbar } = useSnackbar()

  const [orgName, setOrgName] = useState(org?.name)
  const [showInviteDialog, setShowInviteDialog] = useState(false)

  const [updateOrgResult, updateOrg] = useMutation(
    graphql(`
      mutation UpdateOrganizationName($input: UpdateOrgInput!) {
        UpdateOrganization(input: $input) {
          name
          id
        }
      }
    `),
  )

  const [_, removeUser] = useMutation(
    graphql(`
      mutation RemoveUser($userId: Float!) {
        RemoveUserFromOrganization(userId: $userId)
      }
    `),
  )

  useEffect(() => {
    if (updateOrgResult.data) {
      authContext.refreshUser({ requestPolicy: 'network-only' })
    }
  }, [updateOrgResult.data, authContext.refreshUser])

  const [orgUsersQueryResponse, refreshOrgUsersQuery] = useQuery({
    query: graphql(`
      query OrgPageUsers {
        OrgUsers {
          givenName
          familyName
          email
          id
          orgs {
            name
            id
            role
          }
        }
        OrgUserInvitations {
          dateAccepted
          dateCreated
          email
          id
          invitedById
          organizationId
          role
        }
      }`),
  })

  useEventStream({
    onMessage: (message) => {
      console.log('Received message:', message)
      if (message?.event?.startsWith('org.')) {
        console.log('Refetching org users...')
        setTimeout(() => refreshOrgUsersQuery({ requestPolicy: 'network-only' }), 100)
      }
    },
  })

  const flattenedUsersWithRole = useMemo(() => {
    return orgUsersQueryResponse.data?.OrgUsers.map((user) => {
      return {
        ...user,
        role: user.orgs[0].role,
      }
    })
  }, [orgUsersQueryResponse])

  const invitationsRowData = useMemo(() => {
    return orgUsersQueryResponse.data?.OrgUserInvitations.map(serializeDates)
  }, [orgUsersQueryResponse.data?.OrgUserInvitations])

  const handleOrgNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrgName(e.target.value)
  }
  const handleUpdateOrgSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!org || !orgName) return
    updateOrg({ input: { name: orgName.trim() } })
  }
  const handleInviteClick = () => {
    setShowInviteDialog(true)
  }

  const handleInviteDialogClose = () => {
    setShowInviteDialog(false)
  }

  const handleDeleteUserClick = (userId: number) => {
    if (!confirm('Are you sure you want to remove this user?')) {
      return
    }
    removeUser({ userId })
      .then((r) => {
        console.log(r)
        if (r.error) {
          enqueueSnackbar(`Error removing user: ${r.error.message.replace(/\[GraphQL\]/, '')}`, { variant: 'error' })
        } else {
          enqueueSnackbar('User removed', { variant: 'success' })
        }
      })
      .catch((e: Error) => {
        enqueueSnackbar(`Error removing user: ${e.message}`, { variant: 'error' })
      })
  }
  type TData = NonNullable<typeof flattenedUsersWithRole>[0]
  return (
    <>
      <PageHeader title="My Organization" />
      <Grid container spacing={2} maxWidth="sm" mb={4}>
        <Grid size={3} alignItems="center" display="flex">
          <FormLabel>Organization name:</FormLabel>
        </Grid>
        <Grid size={6}>
          <AdminOnly fallback={<Typography>{org?.name}</Typography>}>
            <Stack direction="row" spacing={1} component="form" onSubmit={handleUpdateOrgSubmit}>
              <OutlinedInput fullWidth value={orgName} onChange={handleOrgNameChange} size="small" />
              <Button
                type="submit"
                disabled={orgName === org?.name}
                loading={updateOrgResult.fetching}
                loadingPosition="start"
                startIcon={<SaveIcon />}
                variant="outlined"
              >
                Save
              </Button>
            </Stack>
          </AdminOnly>
        </Grid>
      </Grid>
      <AdminOnly>
        <Typography gutterBottom>Manage your organization's users</Typography>

        <Grid container mb={1} mt={4}>
          <Grid size={10}>
            <Typography variant="h2">Pending Invites</Typography>
          </Grid>
          {invitationsRowData && invitationsRowData.length > 0 && (
            <Grid size={2}>
              <Button variant="contained" color="primary" onClick={handleInviteClick}>
                Invite a user
              </Button>
            </Grid>
          )}
        </Grid>
        {invitationsRowData && invitationsRowData.length === 0 && <NoInvitations onClick={handleInviteClick} />}
        {invitationsRowData && invitationsRowData.length > 0 && (
          <ThemedAgGrid
            rowData={invitationsRowData}
            columnDefs={[
              { field: 'email', headerName: 'Email' },
              { field: 'role', headerName: 'Role' },
              { field: 'dateCreated', headerName: 'Date Created' },
              { field: 'dateAccepted', headerName: 'Date Accepted' },
            ]}
            autoSizeStrategy={{
              type: 'fitGridWidth',
            }}
          />
        )}
        <Typography variant="h2" mt={4}>
          Users
        </Typography>
        <ThemedAgGrid
          columnDefs={[
            { field: 'email', headerName: 'Email' },
            { field: 'role', headerName: 'Role' },
            { field: 'givenName', headerName: 'First Name' },
            { field: 'familyName', headerName: 'Last Name' },
            {
              colId: 'actions',
              headerName: 'Actions',
              resizable: false,
              sortable: false,
              cellRenderer: (d: ICellRendererParams<TData>) => {
                return (
                  <IconButton type="button" color="error" onClick={() => handleDeleteUserClick(d.data!.id)}>
                    <DeleteIcon />
                  </IconButton>
                )
              },
            },
          ]}
          autoSizeStrategy={{
            type: 'fitGridWidth',
          }}
          rowData={flattenedUsersWithRole}
          domLayout="autoHeight"
        />
        <InviteUserDialog dialogProps={{ open: showInviteDialog }} onClose={handleInviteDialogClose} />
      </AdminOnly>
    </>
  )
}

const NoInvitations = (props: { onClick: () => void }) => {
  return (
    <Stack spacing={1} alignItems="center">
      <Typography>You have no invitations pending.</Typography>
      <Button onClick={props.onClick}>Invite a user</Button>
    </Stack>
  )
}
